import React, { useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { Menu, Spin, Typography, Space, Form } from 'antd';
import moment from 'moment';
import DeleteWarningModal from '../../components/Common/DeleteWarningModal';
import AnnouncementsTable from './components/AnnouncementsTable';
import ActionHeader from '../../components/Common/ActionHeader';
import AnnouncementInfo from './components/AnnouncementInfo';
import AnnouncementFormModal from './components/AnnouncementFormModal';
import useCourseOfferAnnouncementsData from '../../hooks/useCourseOfferAnnouncementsData';
import { selectInputParams } from './constants';
import SelectionFormModal from './components/SelectionFormModal/SelectionFormModal';

import './CourseOfferAnnouncementsContainer.scss';

const { Text } = Typography;
const courseOfferAnnouncementsContainerClassName =
  'c-CourseOfferAnnouncementsContainer';

const SingleCourseOfferAnnouncementContainer = () => {
  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [offset, setOffset] = useState((current - 1) * pageSize);
  const [totalCount, setTotalCount] = useState(0);

  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isSelectionVisible, setIsSelectionVisible] = useState(false);

  const history = useHistory();
  const courseOfferAnnouncementId = history.location.pathname.split('/')[2];

  const {
    fetching,
    status,
    trainers,
    selectedCourseOfferAnnouncement,
    fetchSingleCourseOfferAnnouncement,
    sendCourseOfferAnnouncementUpdate,
    selectTrainers,
    retryTrainersFetch,
  } = useCourseOfferAnnouncementsData();

  useEffect(() => {
    fetchSingleCourseOfferAnnouncement(courseOfferAnnouncementId);
  }, [courseOfferAnnouncementId]);

  useEffect(() => {
    setTotalCount(selectedCourseOfferAnnouncement?.bids?.length);
    retryTrainersFetch(selectedCourseOfferAnnouncement?.courseOfferId?._id);
  }, [selectedCourseOfferAnnouncement]);

  const [form] = Form.useForm();
  const [selectionForm] = Form.useForm();

  const onPaginationChange = (data) => {
    setCurrent(data.current);
    setPageSize(data.pageSize);
    setOffset((data.current - 1) * data.pageSize);
  };

  const formatBasicInfo = () => {
    const data = selectedCourseOfferAnnouncement;

    const formattedData = {
      link: `/course-offers/${data?.courseOfferId?.courseOfferId}`,
      courseOfferCode: data?.courseOfferId?.code,
      courseWave: data?.courseOfferId?.wave,
      course: data?.courseOfferId?.courseDescription,
      courseLink: `/course/${data?.courseOfferId?.courseCode}`,
      startDate: moment(data?.courseOfferId?.startDate).format('DD-MM-yyyy'),
      endDate: moment(data?.courseOfferId?.endDate).format('DD-MM-yyyy'),
      startTime: moment(data?.courseOfferId?.startDate).format('LT'),
      endTime: moment(data?.courseOfferId?.endDate).format('LT'),
      frequency: data?.courseOfferId?.frequency || ['Single Session'],
      selectedLeadTrainer: data?.leadTrainer?.selected,
      selectedAssistantTrainer: data?.assistantTrainer?.selected,
      selectedSupport: data?.supportTrainers?.selected,
      preselectMode: `${!!data?.preselectMode}`,
      trainers: data?.trainers,
    };

    return formattedData;
  };

  const onUpdateAnnouncement = (values) => {
    const formatted = Object.assign({}, values, {
      courseOfferId: selectedCourseOfferAnnouncement?.courseOfferId?._id,
      preselectMode: values.preselectMode ? true : false,
    });
    setIsEditModalVisible(false);
    sendCourseOfferAnnouncementUpdate(
      selectedCourseOfferAnnouncement?._id,
      formatted
    );
  };

  const handleOnSelectTrainers = (values) => {
    selectTrainers(values, () =>
      fetchSingleCourseOfferAnnouncement(courseOfferAnnouncementId)
    );
  };

  const handleOnDelete = () => {
    history.push('/course-offer-announcements');
  };

  const getDeleteLink = () =>
    `${process.env.REACT_APP_BACKEND_URL}/course-offer-announcements/${selectedCourseOfferAnnouncement?._id}`;

  const renderActionMenu = () => (
    <Menu>
      <Menu.Item onClick={() => setIsEditModalVisible(true)}>Edit</Menu.Item>
      <Menu.Item onClick={() => setIsDeleteModalVisible(true)}>
        Delete
      </Menu.Item>
      {!selectedCourseOfferAnnouncement.preselectMode && (
        <Menu.Item onClick={() => setIsSelectionVisible(true)}>
          Select Trainers
        </Menu.Item>
      )}
    </Menu>
  );

  const getEditModalInitialValues = () => {
    const courseName =
      selectedCourseOfferAnnouncement?.courseOfferId?.courseDescription;

    return {
      courseId:
        courseName?.length > 25 ? courseName.substr(0, 25) + '...' : courseName,
      courseOfferId: selectedCourseOfferAnnouncement?.courseOfferId?.code,
      usePreselectedTrainer: selectedCourseOfferAnnouncement?.preselectMode,
      preselectedTrainer:
        selectedCourseOfferAnnouncement?.selectedLeadTrainer?._id,
      invitedTrainers: selectedCourseOfferAnnouncement?.trainers?.map(
        (item) => item._id
      ),
    };
  };

  const getSelectModalInitialValues = () => ({
    selectedLeadTrainer:
      selectedCourseOfferAnnouncement?.leadTrainer?.selected?._id,
    selectedAssistantTrainer:
      selectedCourseOfferAnnouncement?.assistantTrainer?.selected?._id,
    selectedSupport: selectedCourseOfferAnnouncement?.supportTrainers?.selected?.map(
      ({ _id }) => _id
    ),
  });

  const getAllBids = () => {
    const formatBids = (data = [], roleLabel) =>
      data?.map((item) => {
        item.roleLabel = roleLabel;

        return item;
      });
    const bids = [
      ...formatBids(
        selectedCourseOfferAnnouncement?.leadTrainer?.bids,
        'Lead Trainer'
      ),
      ...formatBids(
        selectedCourseOfferAnnouncement?.assistantTrainer?.bids,
        'Assistant Trainer'
      ),
      ...formatBids(
        selectedCourseOfferAnnouncement?.supportTrainers?.bids,
        'Support Staff'
      ),
    ];

    return bids;
  };

  const columns = [
    {
      title: 'No',
      dataIndex: 'index',
      key: 'index',
      render: (value, item, index) => <span>{offset + index + 1}</span>,
    },
    {
      title: 'Trainer',
      dataIndex: 'trainer',
      key: 'trainer',
      render: (value, record) => (
        <Space size="middle">
          <Link to={'/trainer/' + record.trainerId}>
            <span>{`${record.firstName} ${record.lastName}`}</span>
          </Link>
        </Space>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Bid Type',
      dataIndex: 'roleLabel',
      key: 'roleLabel',
    },
  ];

  const formatBids = (data) =>
    data?.map((item) => {
      const name = `${item?.firstName} ${item?.lastName}`;
      return { id: item?._id, name };
    });

  if (fetching)
    return (
      <div
        className={`${courseOfferAnnouncementsContainerClassName}__text-center`}
      >
        <Spin />
      </div>
    );

  const getSelectProps = () => {
    const leadTrainerBids =
      formatBids(selectedCourseOfferAnnouncement?.leadTrainer?.bids) || [];
    const assistantTrainerBids =
      formatBids(selectedCourseOfferAnnouncement?.assistantTrainer?.bids) || [];
    const supportTrainersBids =
      formatBids(selectedCourseOfferAnnouncement?.supportTrainers?.bids) || [];

    const bids = [
      leadTrainerBids,
      [...assistantTrainerBids, ...leadTrainerBids],
      [...supportTrainersBids, ...assistantTrainerBids, ...leadTrainerBids],
    ];

    const selectProps = selectInputParams.map((item, i) => {
      item.bids = bids[i];
      return item;
    });

    return selectProps;
  };

  return (
    <div>
      {status.type === 'ERROR' && <Text type="danger">{status.message}</Text>}
      {/* Action Header */}
      <ActionHeader
        title={`Course Offer Announcement for Course Offer Code ${selectedCourseOfferAnnouncement?.courseOfferId?.code}`}
        actionMenu={renderActionMenu()}
      />

      {/* Course Offer Announcement Information */}
      <AnnouncementInfo title={'Info'} data={formatBasicInfo()} />

      {/* Table with Bids */}
      <section>
        <AnnouncementsTable
          data={getAllBids()}
          onPaginationChange={onPaginationChange} // on size change also??
          current={current}
          totalCount={totalCount}
          offset={offset}
          fetching={fetching}
          columns={columns}
        />
      </section>

      {/* Edit modal */}
      <AnnouncementFormModal
        form={form}
        isOpen={isEditModalVisible}
        onClose={() => setIsEditModalVisible(false)}
        status={status}
        courseOffers={[]}
        trainers={trainers}
        onSubmit={onUpdateAnnouncement}
        title="Update Announcement"
        type="Update"
        fetching={fetching}
        courseOfferChangeDisabled={true}
        initialValues={getEditModalInitialValues()}
      />

      {/* Selection modal */}
      {!selectedCourseOfferAnnouncement.preselectMode && (
        <SelectionFormModal
          title="Select Trainers"
          isOpen={isSelectionVisible}
          onClose={() => setIsSelectionVisible(false)}
          onSubmit={handleOnSelectTrainers}
          initialValues={getSelectModalInitialValues()}
          status={status}
          form={selectionForm}
          selectProps={getSelectProps()}
        />
      )}

      {/* Delete Warning modal */}
      <DeleteWarningModal
        isVisible={isDeleteModalVisible}
        setIsVisible={setIsDeleteModalVisible}
        deleteLink={getDeleteLink()}
        cb={handleOnDelete}
        params={{}}
      />
    </div>
  );
};

export default SingleCourseOfferAnnouncementContainer;
